import * as React from "react";

import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  makeStyles,
  MenuItem,
  Select,
  Theme,
  useTheme,
} from "@material-ui/core";
import * as Redux from "react-redux";

import { AllMerchantFields, Field, ValueOf } from "client/lib/apt-merchant";
import { errorActions } from "client/store";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const useStyles = makeStyles((theme: Theme) => ({
  chip: {
    margin: 2,
  },
  wrapper: {
    backgroundColor: theme.palette.common.white,
  },
}));

function getStyles(
  option: ValueOf<AllMerchantFields>,
  selectedValue: ValueOf<AllMerchantFields>[],
  theme: Theme
) {
  return {
    backgroundColor: selectedValue?.includes(option)
      ? theme.palette.primary.main
      : theme.palette.common.white,
    fontWeight: selectedValue?.includes(option)
      ? theme.typography.fontWeightBold
      : theme.typography.fontWeightRegular,
  };
}

interface MultiSelectFieldProps {
  errorMessage?: string;
  field: Field<ValueOf<AllMerchantFields>>;
  hasError: boolean;
  handleChange: (field: Field<any>, value: ValueOf<typeof field>) => void;
}

export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  errorMessage,
  field,
  hasError,
  handleChange,
}) => {
  const dispatch = Redux.useDispatch();

  const theme = useTheme();
  const classes = useStyles();
  const getInitialValue = () => {
    if (typeof field?.value === "string" && Boolean(field?.value)) {
      return field.value.split(";");
    }
    return [];
  };
  const [localSelectedOptions, setLocalSelectedOptions] = React.useState<
    ValueOf<AllMerchantFields>[]
  >(getInitialValue());

  const handleLocalChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    // type guard
    if (Array.isArray(event.target.value)) {
      if (!event.target.value.length) {
        return dispatch(
          errorActions.setError("You must select at least one option")
        );
      }
      setLocalSelectedOptions(event.target.value);
    }
  };

  React.useEffect(() => {
    const formattedValues = localSelectedOptions.join(";");
    handleChange(field, formattedValues);
  }, [localSelectedOptions]);

  return (
    <FormControl
      className={classes.chip}
      error={hasError}
      fullWidth
      variant="outlined"
    >
      <InputLabel id={`${field.propertyName}Label`}>
        {field.i18nValues.label}
      </InputLabel>
      <Select
        labelId={`${field.propertyName}Label`}
        inputProps={{
          className: classes.wrapper,
          id: field.propertyName,
        }}
        label={field.i18nValues.label}
        multiple
        value={localSelectedOptions}
        onChange={handleLocalChange}
        renderValue={(selected) => (
          <Box display="flex" flexWrap="wrap">
            {(selected as string[]).map((value) => (
              <Chip key={value} label={value} className={classes.chip} />
            ))}
          </Box>
        )}
        MenuProps={MenuProps}
      >
        {(field?.pickList ?? []).map((option) => (
          <MenuItem
            style={getStyles(option, localSelectedOptions, theme)}
            key={option as string}
            value={option as string}
          >
            <Checkbox checked={localSelectedOptions.includes(option)} />
            <ListItemText primary={option} />
          </MenuItem>
        ))}
      </Select>
      {(hasError || Boolean(field.i18nValues.helperText)) && (
        <FormHelperText id={field.i18nValues.helperText}>
          {errorMessage || field.i18nValues.helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
